/**
 * 装修器画布 iframe 桥接（仅在含 previewPageId 且被父窗内嵌时启用）
 * 协议：
 *   admin → shop:
 *     { source: 'admin-decoration', type: 'enter-edit-mode', editingPageType }
 *     { source: 'admin-decoration', type: 'highlight', nodeId }
 *     { source: 'admin-decoration', type: 'scroll-to-node', nodeId }
 *     { source: 'admin-decoration', type: 'update-components', pageType, components }
 *     { source: 'admin-decoration', type: 'query-drop-position', y }
 *   shop → admin:
 *     { source: 'shop-decoration', type: 'ready' }
 *     { source: 'shop-decoration', type: 'click', nodeId }
 *     { source: 'shop-decoration', type: 'drop-position', y, insertAtIndex }
 */

import i18n from '@/lang/i18n';
const _t = (key, params) => i18n.t('message.home.' + key, params);
const topPos = (n) => _t('topPos', { n });
const posStr = (n) => _t('pos', { n });
const colStr = (n) => _t('col', { n });

const HIGHLIGHT_CLASS = '__dr-bridge-highlight';
const READONLY_SCOPE_CLASS = '__dr-bridge-readonly-scope';
const STYLE_ID = '__dr-bridge-style';
const TOOLBAR_ID = '__dr-bridge-toolbar';
let active = false;
let editingPageType = null;
let highlightedEl = null;
let toolbarEl = null;
let toolbarScrollHandler = null;
let pendingHighlightNodeId = null; // admin 端在节点出现前就发了 highlight 时，缓存以待重渲后 reapply

function injectStyle () {
  let s = document.getElementById(STYLE_ID);
  if (!s) {
    s = document.createElement('style');
    s.id = STYLE_ID;
    document.head.appendChild(s);
  }
  s.textContent =
    '.' + HIGHLIGHT_CLASS + ' { outline: 2px solid #4d6bfe !important; outline-offset: -2px; box-shadow: 0 0 0 4px rgba(77,107,254,.15); transition: outline .15s; cursor: pointer !important; position: relative; } ' +
    '.decoration-renderer:not(.' + READONLY_SCOPE_CLASS + ') [data-node-id]:hover { outline: 1px dashed #7388ff; outline-offset: -1px; cursor: pointer; } ' +
    '.' + READONLY_SCOPE_CLASS + ' [data-node-id]:hover { outline: none !important; cursor: default !important; } ' +
    '.' + READONLY_SCOPE_CLASS + '.decoration-renderer--preview .dr-node { min-height: 0 !important; outline: none !important; } ' +
    // 只读槽位下不显示常驻 toolbar
    '.' + READONLY_SCOPE_CLASS + ' .__dr-persistent-toolbar { display: none !important; } ' +
    // 选中态的浮动 toolbar
    '.__dr-bridge-toolbar { position: fixed; z-index: 100000; display: flex; align-items: center; gap: 6px; ' +
    'padding: 2px 4px 2px 8px; background: #4d6bfe; color: #fff; border-radius: 4px; ' +
    'font-size: 12px; line-height: 20px; box-shadow: 0 2px 8px rgba(77,107,254,.35); pointer-events: auto; } ' +
    '.__dr-bridge-toolbar__name { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } ' +
    '.__dr-bridge-toolbar__del { border: 0; background: rgba(255,255,255,.2); color: #fff; width: 22px; height: 22px; ' +
    'border-radius: 3px; cursor: pointer; font-size: 16px; line-height: 1; padding: 0; } ' +
    '.__dr-bridge-toolbar__del:hover { background: #f56c6c; } ' +
    // 常驻 toolbar：贴在布局类节点左侧外、垂直居中，不进入控件内
    '.__dr-persistent-toolbar { position: absolute; top: 50%; left: 0; ' +
    'transform: translate(calc(-100% - 8px), -50%); z-index: 50; display: inline-flex; align-items: center; gap: 4px; ' +
    'padding: 1px 4px 1px 8px; background: #4d6bfe; color: #fff; border-radius: 4px; ' +
    'font-size: 12px; line-height: 18px; box-shadow: 0 1px 4px rgba(77,107,254,.3); pointer-events: auto; ' +
    'white-space: nowrap; user-select: none; } ' +
    '.__dr-persistent-toolbar__name { max-width: 140px; overflow: hidden; text-overflow: ellipsis; } ' +
    '.__dr-persistent-toolbar__del { border: 0; background: rgba(255,255,255,.2); color: #fff; width: 18px; height: 18px; ' +
    'border-radius: 3px; cursor: pointer; font-size: 14px; line-height: 1; padding: 0; display: inline-flex; align-items: center; justify-content: center; } ' +
    '.__dr-persistent-toolbar__del:hover { background: #f56c6c; }';
}

function syncEditingScopeAttr () {
  if (editingPageType == null) {
    document.documentElement.removeAttribute('data-dr-editing-scope');
  } else {
    document.documentElement.setAttribute('data-dr-editing-scope', String(editingPageType));
  }
  try {
    window.dispatchEvent(new CustomEvent('decoration-editing-scope', { detail: { editingPageType } }));
  } catch (e) { /* ignore */ }
}

function getScopePageType (el) {
  const root = el && el.closest && el.closest('[data-decoration-scope]');
  if (!root) return null;
  const v = root.getAttribute('data-decoration-scope');
  return v == null || v === '' ? null : Number(v);
}

function isEditableTarget (el) {
  if (editingPageType == null) return true;
  const scope = getScopePageType(el);
  return scope === editingPageType;
}

/** 标记非当前编辑槽位为只读（成熟页展示，不可选中） */
function applyScopeModes () {
  document.querySelectorAll('[data-decoration-scope]').forEach(el => {
    const scope = Number(el.getAttribute('data-decoration-scope'));
    if (editingPageType != null && scope !== editingPageType) {
      el.classList.add(READONLY_SCOPE_CLASS);
    } else {
      el.classList.remove(READONLY_SCOPE_CLASS);
    }
  });
  syncEditingScopeAttr();
}

function scheduleApplyScopeModes () {
  setTimeout(applyScopeModes, 0);
  setTimeout(applyScopeModes, 120);
  setTimeout(applyScopeModes, 500);
}

/** 常驻 toolbar：布局类节点（container/blank/tabs）拖入后即可见，直到节点删除 */
const PERSISTENT_TOOLBAR_TYPES = ['container', 'blank', 'tabs'];
const PERSISTENT_TOOLBAR_CLASS = '__dr-persistent-toolbar';

function ensurePersistentToolbar (el) {
  if (!el || !el.dataset) return;
  const nodeId = el.dataset.nodeId;
  const label = el.dataset.nodeLabel || el.dataset.nodeType || nodeId;
  if (!nodeId) return;
  // 已存在则只更新名字（节点 label 可能因语言切换变化），跳过创建
  const existing = el.querySelector(':scope > .' + PERSISTENT_TOOLBAR_CLASS);
  if (existing) {
    const nameSpanEx = existing.querySelector('.__dr-persistent-toolbar__name');
    if (nameSpanEx && nameSpanEx.textContent !== label) nameSpanEx.textContent = label;
    return;
  }

  const bar = document.createElement('div');
  bar.className = PERSISTENT_TOOLBAR_CLASS;
  bar.setAttribute('data-bridge-ignore', '1');
  const nameSpan = document.createElement('span');
  nameSpan.className = '__dr-persistent-toolbar__name';
  nameSpan.textContent = label;
  const delBtn = document.createElement('button');
  delBtn.type = 'button';
  delBtn.className = '__dr-persistent-toolbar__del';
  delBtn.title = _t('deleteNode');
  delBtn.textContent = '×';
  delBtn.addEventListener('click', (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    postToParent({ type: 'delete-node', nodeId });
  });
  delBtn.addEventListener('mousedown', (ev) => { ev.stopPropagation(); }, true);
  bar.appendChild(nameSpan);
  bar.appendChild(delBtn);

  // 确保父节点为定位上下文
  const cs = window.getComputedStyle(el);
  if (cs.position === 'static') el.style.position = 'relative';

  el.appendChild(bar);
}

function ensureAllPersistentToolbars () {
  if (!active) return;
  const selector = PERSISTENT_TOOLBAR_TYPES.map(t => '[data-node-type="' + t + '"]').join(',');
  const nodes = document.querySelectorAll(selector);
  nodes.forEach(el => {
    // 跳过只读槽位下的节点
    if (el.closest('.' + READONLY_SCOPE_CLASS)) return;
    ensurePersistentToolbar(el);
  });
}

function reapplyPendingHighlight () {
  if (!active || !pendingHighlightNodeId) return;
  // 若当前已 highlight 的元素 id 与待 apply 一致且元素仍存在，则无需重做
  if (highlightedEl && highlightedEl.dataset && highlightedEl.dataset.nodeId === pendingHighlightNodeId &&
      document.body.contains(highlightedEl)) return;
  const el = document.querySelector('[data-node-id="' + pendingHighlightNodeId + '"]');
  if (el && isEditableTarget(el)) highlightNode(pendingHighlightNodeId);
}

function schedulePersistentToolbars () {
  const tick = () => { ensureAllPersistentToolbars(); reapplyPendingHighlight(); pushLayoutToolbars(); };
  setTimeout(tick, 0);
  setTimeout(tick, 120);
  setTimeout(tick, 500);
}

/** 把所有布局类节点的 rect/label/id 推给 admin → admin 在画布外渲染 toolbar */
function pushLayoutToolbars () {
  if (!active) return;
  const selector = PERSISTENT_TOOLBAR_TYPES.map(t => '[data-node-type="' + t + '"]').join(',');
  const nodes = document.querySelectorAll(selector);
  const toolbars = [];
  nodes.forEach(el => {
    if (el.closest('.' + READONLY_SCOPE_CLASS)) return;
    if (!el.dataset || !el.dataset.nodeId) return;
    const r = el.getBoundingClientRect();
    toolbars.push({
      nodeId: el.dataset.nodeId,
      nodeType: el.dataset.nodeType || '',
      label: el.dataset.nodeLabel || el.dataset.nodeType || el.dataset.nodeId,
      rect: { top: r.top, left: r.left, width: r.width, height: r.height }
    });
  });
  postToParent({ type: 'layout-toolbars', toolbars });
}

let _layoutToolbarsScheduled = false;
function scheduleLayoutToolbarsThrottled () {
  if (_layoutToolbarsScheduled) return;
  _layoutToolbarsScheduled = true;
  requestAnimationFrame(() => {
    _layoutToolbarsScheduled = false;
    pushLayoutToolbars();
  });
}

function removeToolbar () {
  if (toolbarScrollHandler) {
    window.removeEventListener('scroll', toolbarScrollHandler, true);
    toolbarScrollHandler = null;
  }
  if (toolbarEl) {
    toolbarEl.remove();
    toolbarEl = null;
  }
}

function positionToolbar (el) {
  if (!toolbarEl || !el) return;
  const rect = el.getBoundingClientRect();
  const isContainer = el.dataset && el.dataset.nodeType === 'container';
  if (isContainer) {
    const tw = toolbarEl.offsetWidth || 72;
    toolbarEl.style.top = Math.max(4, rect.top + 8) + 'px';
    toolbarEl.style.left = Math.max(4, rect.left - tw - 8) + 'px';
  } else {
    toolbarEl.style.top = Math.max(4, rect.top - 28) + 'px';
    toolbarEl.style.left = Math.max(4, rect.left) + 'px';
  }
}

function showToolbar (el, nodeId) {
  removeToolbar();
  if (!el || !active) return;
  // 布局类节点（容器/辅助留白/Tab切换）已有常驻 toolbar 显示，避免重复
  const nodeType = el.dataset && el.dataset.nodeType;
  if (PERSISTENT_TOOLBAR_TYPES.indexOf(nodeType) >= 0) return;
  const label = (el.dataset && (el.dataset.nodeLabel || el.dataset.nodeType)) || nodeId;
  toolbarEl = document.createElement('div');
  toolbarEl.id = TOOLBAR_ID;
  toolbarEl.className = '__dr-bridge-toolbar';
  const nameSpan = document.createElement('span');
  nameSpan.className = '__dr-bridge-toolbar__name';
  nameSpan.textContent = label;
  const delBtn = document.createElement('button');
  delBtn.type = 'button';
  delBtn.className = '__dr-bridge-toolbar__del';
  delBtn.title = _t('deleteNode');
  delBtn.textContent = '×';
  delBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    postToParent({ type: 'delete-node', nodeId });
  });
  toolbarEl.appendChild(nameSpan);
  toolbarEl.appendChild(delBtn);
  document.body.appendChild(toolbarEl);
  positionToolbar(el);
  toolbarScrollHandler = () => positionToolbar(el);
  window.addEventListener('scroll', toolbarScrollHandler, true);
}

function findNodeIdFromTarget (el) {
  let cur = el;
  while (cur && cur !== document.body) {
    if (cur.dataset && cur.dataset.nodeId) {
      if (!isEditableTarget(cur)) return null;
      return cur.dataset.nodeId;
    }
    cur = cur.parentElement;
  }
  return null;
}

function postToParent (msg) {
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(Object.assign({ source: 'shop-decoration' }, msg), '*');
    }
  } catch (e) { /* 跨域受限静默 */ }
}

function isInteractiveTarget (el) {
  if (!el || !el.closest) return false;
  return !!el.closest(
    'input, textarea, select, button, [contenteditable="true"], a[href], ' +
    '.el-input, .el-textarea, .el-input__inner, .el-input__wrapper, .el-button, ' +
    '.dr-searchnav, .dr-search, .dr-search__box, .dr-biz-search, label'
  );
}

function onClickInEditMode (e) {
  if (isInteractiveTarget(e.target)) return;
  const id = findNodeIdFromTarget(e.target);
  if (id) {
    e.preventDefault();
    const isTabLabel = e.target && (e.target.classList.contains('dr-tabs__label') ||
      (e.target.closest && !!e.target.closest('.dr-tabs__label')));
    if (!isTabLabel) {
      e.stopPropagation();
    }
    postToParent({ type: 'click', nodeId: id });
  }
}

function clearHighlight () {
  if (highlightedEl) {
    highlightedEl.classList.remove(HIGHLIGHT_CLASS);
    highlightedEl = null;
  }
  removeToolbar();
}

function highlightNode (nodeId) {
  clearHighlight();
  if (!nodeId) return;
  const el = document.querySelector('[data-node-id="' + nodeId + '"]');
  if (!el || !isEditableTarget(el)) return;
  el.classList.add(HIGHLIGHT_CLASS);
  highlightedEl = el;
  showToolbar(el, nodeId);
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function getEditableRenderer () {
  if (editingPageType == null) {
    return document.querySelector('.decoration-renderer:not(.' + READONLY_SCOPE_CLASS + ')') ||
      document.querySelector('.decoration-renderer');
  }
  return document.querySelector('.decoration-renderer[data-decoration-scope="' + editingPageType + '"]');
}

/**
 * 根据 y/x 坐标计算插入位置，支持容器列内嵌套（仅当前编辑槽位内）。
 *
 * 修复"容器上下闪烁"：Y 一旦进入容器范围 → **总是 snap 到最近列**，
 * 不再因 X 不在任何列内而 fall through 触发"前/后"的来回切换。
 */
const EDGE_BUFFER = 10;  // 容器/tabs 上下边缘留白：Y 在边缘 buffer 内触发"前/后"提示

/** 把 DOMRect 转成可序列化的 plain object（postMessage 不能传 DOMRect 实例） */
function serializeRect (r) {
  if (!r) return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function computeDropPosition (y, x, excludeNodeId) {
  const renderer = getEditableRenderer();
  if (!renderer) return { y, insertAtIndex: -1, pathLabels: [] };
  const nodes = Array.from(renderer.querySelectorAll(':scope > [data-node-id]'))
    .filter(n => !excludeNodeId || n.dataset.nodeId !== excludeNodeId);
  if (!nodes.length) {
    const rRect = renderer.getBoundingClientRect();
    return { y: rRect.top, insertAtIndex: 0, pathLabels: [topPos(1)] };
  }

  for (let i = 0; i < nodes.length; i++) {
    const el = nodes[i];
    const rect = el.getBoundingClientRect();

    // Y 在该节点上方 → 插入到该节点之前
    if (y < rect.top) {
      return { y: rect.top, insertAtIndex: i, pathLabels: [topPos(i + 1)] };
    }

    // Y 在该节点范围内
    if (y <= rect.bottom) {
      const nodeType = el.dataset.nodeType;
      if (nodeType === 'container' || nodeType === 'tabs') {
        // 容器边缘 buffer：Y 在容器顶 EDGE_BUFFER 内 → 插到容器之前
        if (y < rect.top + EDGE_BUFFER) {
          return { y: rect.top, insertAtIndex: i, pathLabels: [topPos(i + 1)] };
        }
        // Y 在容器底 EDGE_BUFFER 内 → 插到容器之后
        if (y > rect.bottom - EDGE_BUFFER) {
          return { y: rect.bottom, insertAtIndex: i + 1, pathLabels: [topPos(i + 2)] };
        }
        // 中间区域：snap 到列内
        return resolveContainerDrop(el, nodeType, y, x, i, excludeNodeId);
      }
      // 普通节点：按中点分前/后
      if (y < rect.top + rect.height / 2) {
        return { y: rect.top, insertAtIndex: i, pathLabels: [topPos(i + 1)] };
      }
      // Y 在下半部分 → continue 到下一节点（= 插到下一节点之前 = 此节点之后）
    }
    // Y 在该节点下方 → continue
  }
  // 所有节点都在 Y 上方 → 末尾
  const last = nodes[nodes.length - 1].getBoundingClientRect();
  return { y: last.bottom, insertAtIndex: nodes.length, pathLabels: [topPos(nodes.length + 1)] };
}

/** 容器/tabs 节点的子列插入定位：总是 snap 到最近的列 */
function resolveContainerDrop (el, nodeType, y, x, topLevelIdx, excludeNodeId) {
  const elRect = el.getBoundingClientRect();
  const containerLabel = (el.dataset && el.dataset.nodeLabel) || (nodeType === 'tabs' ? _t('decoType_tabs') : _t('decoType_container'));
  let colEl = null;
  let colIndex = 0;
  let colLabel = '';

  if (nodeType === 'container') {
    const cols = Array.from(el.querySelectorAll('.dr-col'));
    if (!cols.length) {
      return {
        y: elRect.bottom,
        insertAtIndex: topLevelIdx + 1,
        pathLabels: [topPos(topLevelIdx + 2)]
      };
    }
    let nearestDist = Infinity;
    for (let ci = 0; ci < cols.length; ci++) {
      const cr = cols[ci].getBoundingClientRect();
      const cx = (cr.left + cr.right) / 2;
      const dist = Math.abs((x == null ? cx : x) - cx);
      if (dist < nearestDist) { nearestDist = dist; colIndex = ci; colEl = cols[ci]; }
    }
    colLabel = colStr(colIndex + 1);
  } else {
    const tabContent = el.querySelector('.decoration-renderer');
    if (!tabContent) {
      return {
        y: elRect.bottom,
        insertAtIndex: topLevelIdx + 1,
        pathLabels: [topPos(topLevelIdx + 2)]
      };
    }
    colEl = tabContent;
    const labels = Array.from(el.querySelectorAll('.dr-tabs__label'));
    const activeIdx = labels.findIndex(l => l.classList.contains('is-active'));
    colIndex = Math.max(0, activeIdx);
    colLabel = 'Tab ' + (colIndex + 1);
  }

  const colRect = colEl.getBoundingClientRect();
  // 阶段 9.B 修复：container 的 .dr-col 直接子是 <div class="decoration-renderer">，
  // 节点在更深一层。tabs 的 colEl 本身就是 .decoration-renderer。统一穿透 → 取内层 [data-node-id]。
  const itemsContainer = colEl.classList.contains('decoration-renderer')
    ? colEl
    : (colEl.querySelector(':scope > .decoration-renderer') || colEl);
  const colItems = Array.from(itemsContainer.querySelectorAll(':scope > [data-node-id]'))
    .filter(n => !excludeNodeId || n.dataset.nodeId !== excludeNodeId);
  let colInsertAt = colItems.length;
  let hintY = colRect.bottom;
  for (let j = 0; j < colItems.length; j++) {
    const nr = colItems[j].getBoundingClientRect();
    if (y < nr.top + nr.height / 2) {
      colInsertAt = j;
      hintY = nr.top;
      break;
    }
  }
  return {
    y: hintY,
    insertAtIndex: topLevelIdx,
    containerId: el.dataset.nodeId,
    colIndex,
    containerInsertAt: colInsertAt,
    containerRect: serializeRect(elRect),
    colRect: serializeRect(colRect),
    pathLabels: [containerLabel, colLabel, posStr(colInsertAt + 1)]
  };
}

/** ---------- 现有控件拖动（move-node 协议）---------- */

const DRAG_THRESHOLD = 6;
let dragIndicator = null;
const moveDrag = { active: false, startX: 0, startY: 0, nodeId: '' };

function ensureDragIndicator () {
  if (dragIndicator) return dragIndicator;
  dragIndicator = document.createElement('div');
  dragIndicator.id = '__dr-bridge-drag-indicator';
  dragIndicator.style.cssText = 'position: fixed; left: 0; right: 0; height: 3px; ' +
    'background: #4d6bfe; pointer-events: none; z-index: 99998; ' +
    'box-shadow: 0 0 8px rgba(77,107,254,.6); display: none;';
  document.body.appendChild(dragIndicator);
  return dragIndicator;
}

function onMouseDownDrag (ev) {
  if (!active) return;
  if (ev.button !== 0) return;
  if (isInteractiveTarget(ev.target)) return;
  const id = findNodeIdFromTarget(ev.target);
  if (!id) return;
  moveDrag.active = false;
  moveDrag.startX = ev.clientX;
  moveDrag.startY = ev.clientY;
  moveDrag.nodeId = id;
  // 不 preventDefault：让 click 仍能正常发（选中）；只在 mousemove 超阈值才激活拖动
}

function onMouseMoveDrag (ev) {
  if (!moveDrag.nodeId) return;
  if (!moveDrag.active) {
    const dx = ev.clientX - moveDrag.startX;
    const dy = ev.clientY - moveDrag.startY;
    if (dx * dx + dy * dy < DRAG_THRESHOLD * DRAG_THRESHOLD) return;
    moveDrag.active = true;
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    ensureDragIndicator();
  }
  const pos = computeDropPosition(ev.clientY, ev.clientX, moveDrag.nodeId);
  if (pos && pos.insertAtIndex >= 0) {
    dragIndicator.style.top = pos.y + 'px';
    dragIndicator.style.display = 'block';
    moveDrag.lastPos = pos;
    // 阶段 9.B：把同一份 pos 主动推给 admin → 复用 admin overlay 的粗横线/路径文字/容器高亮
    postToParent({
      type: 'drop-position',
      y: pos.y,
      insertAtIndex: pos.insertAtIndex,
      containerId: pos.containerId || null,
      colIndex: pos.colIndex || 0,
      containerInsertAt: pos.containerInsertAt != null ? pos.containerInsertAt : -1,
      containerRect: pos.containerRect || null,
      colRect: pos.colRect || null,
      pathLabels: pos.pathLabels || []
    });
  }
}

function onMouseUpDrag (ev) {
  if (!moveDrag.nodeId) return;
  const wasActive = moveDrag.active;
  const sourceId = moveDrag.nodeId;
  const pos = moveDrag.lastPos;
  moveDrag.active = false;
  moveDrag.nodeId = '';
  moveDrag.lastPos = null;
  if (dragIndicator) dragIndicator.style.display = 'none';
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
  if (wasActive && pos) {
    ev.preventDefault();
    ev.stopPropagation();
    postToParent({
      type: 'move-node',
      sourceNodeId: sourceId,
      insertAtIndex: pos.insertAtIndex,
      containerId: pos.containerId || null,
      colIndex: pos.colIndex || 0,
      containerInsertAt: pos.containerInsertAt != null ? pos.containerInsertAt : -1
    });
  }
  // 阶段 9.B：通知 admin 清掉 dropHint 浮层（无论是否激活拖动）
  postToParent({ type: 'drop-hint-hide' });
}

/** Esc / window blur 兜底：用户中途取消拖动也清浮层 */
function onMoveDragCancel () {
  if (!moveDrag.nodeId) return;
  moveDrag.active = false;
  moveDrag.nodeId = '';
  moveDrag.lastPos = null;
  if (dragIndicator) dragIndicator.style.display = 'none';
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
  postToParent({ type: 'drop-hint-hide' });
}
function onMoveDragKey (e) {
  if (e.key === 'Escape') onMoveDragCancel();
}

function onAdminMessage (event) {
  const data = event.data;
  if (!data || data.source !== 'admin-decoration') return;
  if (data.type === 'enter-edit-mode') {
    editingPageType = data.editingPageType != null ? Number(data.editingPageType) : null;
    if (!active) {
      active = true;
      injectStyle();
      document.addEventListener('click', onClickInEditMode, true);
      // 现有控件拖动（move-node）
      document.addEventListener('mousedown', onMouseDownDrag, true);
      document.addEventListener('mousemove', onMouseMoveDrag, true);
      document.addEventListener('mouseup', onMouseUpDrag, true);
      // 阶段 9.B：Esc / blur 兜底清浮层
      document.addEventListener('keydown', onMoveDragKey, true);
      window.addEventListener('blur', onMoveDragCancel);
      // 阶段 9.C：iframe scroll/resize 时刷新 admin 端的 layout toolbar 位置
      window.addEventListener('scroll', scheduleLayoutToolbarsThrottled, true);
      window.addEventListener('resize', scheduleLayoutToolbarsThrottled);
    }
    scheduleApplyScopeModes();
    schedulePersistentToolbars();
  } else if (data.type === 'highlight') {
    pendingHighlightNodeId = data.nodeId || null;
    highlightNode(data.nodeId);
    // 节点可能还未渲染（admin 先发 highlight 后发 update-components）→ 兜底重试
    if (data.nodeId && !highlightedEl) {
      setTimeout(reapplyPendingHighlight, 120);
      setTimeout(reapplyPendingHighlight, 350);
    }
  } else if (data.type === 'scroll-to-node') {
    const el = data.nodeId && document.querySelector('[data-node-id="' + data.nodeId + '"]');
    if (el && isEditableTarget(el)) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } else if (data.type === 'update-components') {
    try {
      window.dispatchEvent(new CustomEvent('decoration-live-update', {
        detail: {
          pageType: data.pageType,
          components: Array.isArray(data.components) ? data.components : []
        }
      }));
      scheduleApplyScopeModes();
      schedulePersistentToolbars();
      if (data.scrollToNodeId) {
        setTimeout(() => {
          const el = document.querySelector('[data-node-id="' + data.scrollToNodeId + '"]');
          if (el && isEditableTarget(el)) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    } catch (e) { /* CustomEvent 不支持的旧浏览器静默 */ }
  } else if (data.type === 'query-drop-position') {
    const pos = computeDropPosition(Number(data.y) || 0, data.x != null ? Number(data.x) : null);
    postToParent({
      type: 'drop-position',
      y: pos.y,
      insertAtIndex: pos.insertAtIndex,
      containerId: pos.containerId || null,
      colIndex: pos.colIndex || 0,
      containerInsertAt: pos.containerInsertAt != null ? pos.containerInsertAt : -1,
      containerRect: pos.containerRect || null,
      colRect: pos.colRect || null,
      pathLabels: pos.pathLabels || []
    });
  }
}

/**
 * 启用桥接：仅在 (1) 含 previewPageId 且 (2) 被父窗内嵌时执行。
 */
export function enableDecorationBridge () {
  if (typeof window === 'undefined') return;
  const isFramed = window.parent && window.parent !== window;
  const hasPreview = /[?&]previewPageId=/.test(window.location.href);
  if (!isFramed || !hasPreview) return;
  window.addEventListener('message', onAdminMessage);
  postToParent({ type: 'ready' });
}
